/**
 * ChannelHighlights Component
 * Displays channel performance highlights from AI insights
 */
import {
  BarChart2,
  type LucideIcon,
  Mail,
  Megaphone,
  Monitor,
  MousePointerClick,
  Play,
  Search,
  Share2,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { useSettingsStore } from "../../../stores/settingsStore";
import { ChannelCharacteristic } from "../types";

/** Map common GA4 / marketing channel keys to an icon. Case-insensitive substring match. */
const CHANNEL_ICON_MAP: Array<{ pattern: RegExp; Icon: LucideIcon }> = [
  { pattern: /organic.?search|seo/i, Icon: Search },
  {
    pattern: /paid.?search|ppc|cpc|google.?ads|bing/i,
    Icon: MousePointerClick,
  },
  { pattern: /paid.?social/i, Icon: Megaphone },
  { pattern: /organic.?social|social/i, Icon: Share2 },
  { pattern: /email/i, Icon: Mail },
  { pattern: /display|banner/i, Icon: Monitor },
  { pattern: /video|youtube/i, Icon: Play },
  { pattern: /referral/i, Icon: Users },
];

function getChannelIcon(channelKey: string): LucideIcon {
  for (const { pattern, Icon } of CHANNEL_ICON_MAP) {
    if (pattern.test(channelKey)) return Icon;
  }
  return BarChart2;
}

/** Per-channel fee context from responseData.metrics.fee_context */
export type FeeContext = Record<
  string,
  { fee_included: boolean; fee_percent: number }
>;

interface ChannelHighlightsProps {
  channelCharacteristics?: ChannelCharacteristic[] | undefined;
  /** Per-channel fee context: { google_ads: { fee_included, fee_percent }, ... } */
  feeContext?: FeeContext | undefined;
}

export default function ChannelHighlights({
  channelCharacteristics,
}: ChannelHighlightsProps) {
  const { t } = useTranslation();
  const darkMode = useSettingsStore((state) => state.darkMode);

  // If no data provided, show placeholder
  if (!channelCharacteristics || channelCharacteristics.length === 0) {
    return (
      <div
        className={`rounded-3xl p-4 sm:p-6 border ${
          darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        }`}
      >
        <h3
          className={`text-base sm:text-lg font-bold mb-4 ${darkMode ? "text-white" : "text-gray-900"}`}
        >
          {t("reports.channelHighlights")}
        </h3>
        <div className="text-center py-8">
          <p
            className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            {t("reports.noDataAvailable") || "No channel data available"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-3xl p-4 sm:p-6 border ${
        darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      <h3
        className={`text-base sm:text-lg font-bold mb-4 ${darkMode ? "text-white" : "text-gray-900"}`}
      >
        {t("reports.channelHighlights")}
      </h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {channelCharacteristics.map((channel, index) => {
          const colorScheme = index % 2 === 0 ? "primary" : "secondary";
          const ChannelIcon = getChannelIcon(channel.channel);

          return (
            <div
              key={channel.channel}
              className={`bg-gradient-to-br rounded-2xl p-4 border ${
                darkMode
                  ? colorScheme === "primary"
                    ? "from-[var(--brand-primary)]/10 to-[var(--brand-primary)]/5 border-[var(--brand-primary)]/30"
                    : "from-[var(--brand-secondary)]/10 to-[var(--brand-secondary)]/5 border-[var(--brand-secondary)]/30"
                  : colorScheme === "primary"
                    ? "from-[var(--brand-primary)]/5 to-[var(--brand-primary)]/10 border-[var(--brand-primary)]/20"
                    : "from-[var(--brand-secondary)]/5 to-[var(--brand-secondary)]/10 border-[var(--brand-secondary)]/20"
              }`}
            >
              {/* Channel Title with Icon */}
              <div className="flex items-center gap-2 mb-2">
                <span
                  className={`flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0 ${
                    colorScheme === "primary"
                      ? "bg-[var(--brand-primary)]/20 text-[var(--brand-primary)]"
                      : "bg-[var(--brand-secondary)]/20 text-[var(--brand-secondary)]"
                  }`}
                >
                  <ChannelIcon className="w-4 h-4" />
                </span>
                <h4
                  className={`text-sm font-bold ${darkMode ? "text-white" : "text-gray-900"}`}
                >
                  {channel.display_name}
                </h4>
              </div>

              {/* Summary */}
              <p
                className={`text-xs sm:text-sm leading-relaxed ${darkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                {channel.summary}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
